// @ts-nocheck — frozen design snapshot, intentionally not type-checked
/**
 * AMZEL Nest — Site Detail v2 (Design Preview)
 *
 * Alternative site-detail page for PAD2. Designed for an Amazon operations
 * manager or auditor to learn the key facts of a site in 10 seconds and
 * drill into any section in 1 click — without a 10-tab IT cockpit.
 *
 * Layout:
 *   - Hero: Site code, name, classification, address, "last visit"
 *   - Big "at a glance" facts (Hardware, Antennas, Repeater online, Freq end)
 *   - Section cards in a 2-column grid (Hardware | Frequenzen | Codeplugs |
 *     Heatmaps | Crew | Dokumente). Click expands; nothing forces the user
 *     into a tab labyrinth.
 *
 * Data: PAD2 mock from `../data/pad2.ts` (read-only, do not mutate).
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  Antenna,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  FileText,
  History,
  MapPin,
  RadioTower,
  ShieldCheck,
  Users,
} from "lucide-react";
import { PAD2 } from "../data/pad2";
import { ShellV2 } from "./shell-v2";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function daysUntil(iso: string): number {
  const target = new Date(iso + "T00:00:00").getTime();
  const today = new Date().setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function fmtKb(kb: number): string {
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${kb} KB`;
}

const SITE_TYPE_LABELS: Record<string, string> = {
  FC: "Fulfillment Center",
  AMZL: "Last-Mile-Standort",
  AMXL: "Heavy & Bulky",
  ATS: "Transportstandort",
  VENDORFLEX: "Vendor Flex",
  SORTCENTER: "Sort Center",
  OTHER: "Sonstige",
};

const ACTIVITY_LABELS: Record<string, string> = {
  install: "Installation",
  maintenance: "Wartung",
  survey: "Coverage-Survey",
  audit: "Audit",
  repair: "Reparatur",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SiteDetailV2() {
  const { code } = useParams<{ code: string }>();

  // Only PAD2 is mocked. Real backend follows in Phase 1.
  const isPad2 = (code ?? "PAD2").toUpperCase() === "PAD2";
  if (!isPad2) {
    return (
      <ShellV2 back={{ to: "/preview/landing-v2", label: "Zur Übersicht" }}>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <h1 className="text-2xl font-bold tracking-tight">
            Detail-Seite „{code}" noch nicht verfügbar
          </h1>
          <p className="mt-2 text-base">
            Im Design-Preview ist nur <strong>PAD2</strong> vollständig mit
            Beispieldaten hinterlegt. Springe zu{" "}
            <a
              className="font-semibold underline underline-offset-4"
              href="/preview/sites/PAD2/v2"
            >
              PAD2
            </a>
            {"."}
          </p>
        </div>
      </ShellV2>
    );
  }

  const site = PAD2;
  const onlineRepeaters = site.repeaters.filter((r) => r.online).length;
  const offlineRepeaters = site.repeaters.length - onlineRepeaters;
  const earliestFreqExpiry = [...site.frequencies].sort((a, b) =>
    a.valid_until.localeCompare(b.valid_until),
  )[0];
  const lastVisit = site.crew_log.at(-1) ?? site.crew_log[0];
  const deployedCodeplug = site.codeplugs.find((c) => c.is_deployed) ?? site.codeplugs[0];
  const heatmapDocs = site.documents.filter((d) => d.type === "heatmap_pdf");
  const totalDocs = site.documents.length;
  const annualFreqCost = site.frequencies.reduce(
    (acc, f) => acc + f.annual_cost_eur,
    0,
  );
  const expiresInDays = daysUntil(earliestFreqExpiry.valid_until);

  return (
    <ShellV2
      kicker={site.code}
      back={{ to: "/preview/landing-v2", label: "Zur Übersicht" }}
    >
      {/* Hero */}
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                <CircleDot className="size-3" aria-hidden />
                {site.operational_status === "active" ? "Aktiv im Betrieb" : site.operational_status}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {SITE_TYPE_LABELS[site.business_unit] ?? site.business_unit}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {site.country}
              </span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              {site.code}
            </h1>
            <p className="text-xl text-slate-700 dark:text-slate-200">
              {site.name.replace(`${site.code} — `, "")}
            </p>
            <p className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
              <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{site.address}</span>
            </p>
          </div>

          <div className="rounded-2xl bg-slate-50 p-5 text-sm sm:min-w-[280px] dark:bg-slate-800/60">
            <div className="grid grid-cols-1 gap-3">
              <ContactRow label="Spreenauten POC" value={site.poc_spreenauten} />
              <ContactRow label="Amazon POC" value={site.poc_amazon} />
              <ContactRow
                label="Letzter Vor-Ort-Besuch"
                value={`${lastVisit.visitor} · ${formatDate(lastVisit.date)}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* At-a-glance KPI */}
      <section className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <BigFact
          icon={<RadioTower className="size-5" aria-hidden />}
          label="Repeater"
          primary={`${site.repeaters.length}× SLR5500`}
          secondary={
            offlineRepeaters > 0
              ? `${onlineRepeaters} online · ${offlineRepeaters} offline`
              : `${onlineRepeaters} online`
          }
          tone={offlineRepeaters > 0 ? "warn" : "ok"}
        />
        <BigFact
          icon={<Antenna className="size-5" aria-hidden />}
          label="Antennen"
          primary={`${site.antennas.total} Stück`}
          secondary={`${site.antennas.indoor} indoor · ${site.antennas.outdoor} outdoor`}
        />
        <BigFact
          icon={<CalendarClock className="size-5" aria-hidden />}
          label="Frequenz-Lizenz"
          primary={`Gültig bis ${formatDate(earliestFreqExpiry.valid_until)}`}
          secondary={
            expiresInDays > 0
              ? `noch ${Math.round(expiresInDays / 30)} Monate`
              : "abgelaufen!"
          }
          tone={expiresInDays < 90 ? "warn" : "ok"}
        />
        <BigFact
          icon={<ShieldCheck className="size-5" aria-hidden />}
          label="Codeplug"
          primary={deployedCodeplug.version}
          secondary={`Stand ${formatDate(deployedCodeplug.committed_at)}`}
        />
      </section>

      {/* Section cards */}
      <section className="mt-10 grid gap-4 lg:grid-cols-2">
        <DetailSection
          icon={<RadioTower className="size-5" aria-hidden />}
          title="Hardware-Inventar"
          subtitle={`${site.repeaters.length} Repeater · ${site.antennas.total} Antennen`}
        >
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {site.repeaters.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{r.id}</span>
                    {r.role === "master" && (
                      <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white dark:bg-white dark:text-slate-900">
                        Master
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {r.model} · Firmware {r.firmware}
                  </div>
                </div>
                {r.online ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="size-4" aria-hidden /> Online
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-700 dark:text-rose-400">
                    <AlertTriangle className="size-4" aria-hidden /> Offline
                  </span>
                )}
              </li>
            ))}
          </ul>
          <FactStrip>
            <Fact label="Antennen innen" value={site.antennas.indoor} />
            <Fact label="Antennen außen" value={site.antennas.outdoor} />
            <Fact label="Gesamt" value={site.antennas.total} />
          </FactStrip>
        </DetailSection>

        <DetailSection
          icon={<CalendarClock className="size-5" aria-hidden />}
          title="Frequenzzuteilung"
          subtitle={`${site.frequencies.length} Kanäle · Behörde ${earliestFreqExpiry.authority}`}
        >
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {site.frequencies.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between py-3"
              >
                <div>
                  <div className="font-mono text-sm font-semibold">
                    {f.rx.toFixed(4)} / {f.tx.toFixed(4)} MHz
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {f.ref} · {f.bandwidth_khz} kHz
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium text-slate-700 dark:text-slate-200">
                    bis {formatDate(f.valid_until)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Kosten: Amazon · {f.annual_cost_eur} €/Jahr
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <FactStrip>
            <Fact label="Behörde" value={earliestFreqExpiry.authority} />
            <Fact label="Kostenträger" value="Amazon" />
            <Fact label="Gesamtkosten p.a." value={`${annualFreqCost} €`} />
          </FactStrip>
        </DetailSection>

        <DetailSection
          icon={<ShieldCheck className="size-5" aria-hidden />}
          title="Codeplug-Versionen"
          subtitle={`Aktuell ausgerollt: ${deployedCodeplug.version}`}
        >
          <ul className="space-y-3">
            {site.codeplugs.map((c) => (
              <li
                key={c.version}
                className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{c.version}</span>
                    {c.is_deployed && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                        ausgerollt
                      </span>
                    )}
                    {c.is_standard && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                        EU-Standard
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(c.committed_at)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {c.message}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  von {c.committed_by}
                </p>
              </li>
            ))}
          </ul>
        </DetailSection>

        <DetailSection
          icon={<MapPin className="size-5" aria-hidden />}
          title="Coverage-Heatmaps"
          subtitle={`${heatmapDocs.length} Karten verfügbar`}
        >
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Letzte Coverage-Survey:{" "}
            <strong className="font-semibold text-slate-900 dark:text-slate-100">
              {formatDate(site.last_survey_at)}
            </strong>{" "}
            durch {site.poc_spreenauten.split(" (")[0]}.
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {heatmapDocs.map((d) => (
              <li
                key={d.name}
                className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-sm dark:border-slate-800 dark:bg-slate-800/30"
              >
                <FileText className="size-4 text-slate-400" aria-hidden />
                <span className="min-w-0 flex-1 truncate" title={d.name}>
                  {d.name}
                </span>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                  {fmtKb(d.size_kb)}
                </span>
              </li>
            ))}
          </ul>
        </DetailSection>

        <DetailSection
          icon={<Users className="size-5" aria-hidden />}
          title="Crew-History"
          subtitle={`${site.crew_log.length} Einsätze dokumentiert`}
        >
          <ol className="relative ml-3 space-y-4 border-l border-slate-200 pl-5 dark:border-slate-700">
            {[...site.crew_log].reverse().map((v, idx) => (
              <li key={`${v.date}-${v.visitor}-${idx}`} className="relative">
                <span
                  className="absolute -left-[26px] top-1.5 size-3 rounded-full border-2 border-white bg-slate-300 dark:border-slate-900 dark:bg-slate-600"
                  aria-hidden
                />
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold">{v.visitor}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(v.date)} · {ACTIVITY_LABELS[v.activity] ?? v.activity}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
                  {v.summary}
                </p>
              </li>
            ))}
          </ol>
        </DetailSection>

        <DetailSection
          icon={<FileText className="size-5" aria-hidden />}
          title="Dokumente"
          subtitle={`${totalDocs} PDFs & Pläne verfügbar`}
        >
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {site.documents.map((d) => (
              <li
                key={d.name}
                className="flex items-center justify-between py-2.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText
                    className="size-4 shrink-0 text-slate-400"
                    aria-hidden
                  />
                  <span className="truncate text-sm" title={d.name}>
                    {d.name}
                  </span>
                </div>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                  {fmtKb(d.size_kb)}
                </span>
              </li>
            ))}
          </ul>
        </DetailSection>
      </section>

      {/* Footer narrative */}
      <section className="mt-10 rounded-2xl bg-slate-100 p-6 text-sm leading-relaxed text-slate-700 sm:p-8 dark:bg-slate-800/40 dark:text-slate-300">
        <div className="flex items-start gap-3">
          <History className="mt-0.5 size-5 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
          <div>
            <p className="font-semibold text-slate-900 dark:text-slate-100">
              Was diese Seite leistet
            </p>
            <p className="mt-1">
              AMZEL Nest bündelt für jeden Amazon-Standort Hardware, Lizenzen,
              Codeplugs, Heatmaps und Vor-Ort-Historie — eine Quelle der
              Wahrheit für Operations, Audit und AMZ-Infosec. Alle Daten in
              dieser Demo stammen aus den As-Built-Unterlagen für PAD2 vom{" "}
              {formatDate(site.last_survey_at)}.
            </p>
          </div>
        </div>
      </section>
    </ShellV2>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ContactRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
        {value}
      </span>
    </div>
  );
}

const BIG_FACT_TONE_CLASSES = {
  warn: "text-amber-700 dark:text-amber-400",
  ok: "text-emerald-700 dark:text-emerald-400",
  default: "text-slate-900 dark:text-slate-100",
} as const;

function BigFact({
  icon,
  label,
  primary,
  secondary,
  tone,
}: Readonly<{
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string;
  tone?: "ok" | "warn";
}>) {
  const accent = BIG_FACT_TONE_CLASSES[tone ?? "default"];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <span className="text-slate-400 dark:text-slate-500">{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`mt-2 text-xl font-bold tracking-tight ${accent}`}>
        {primary}
      </div>
      {secondary && (
        <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {secondary}
        </div>
      )}
    </div>
  );
}

function DetailSection({
  icon,
  title,
  subtitle,
  children,
}: Readonly<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}>) {
  const [open, setOpen] = useState(true);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-2xl p-5 text-left transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex size-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            {icon}
          </span>
          <div>
            <div className="text-base font-semibold tracking-tight">{title}</div>
            {subtitle && (
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <ChevronDown
          aria-hidden
          className={`size-5 shrink-0 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="border-t border-slate-100 p-5 dark:border-slate-800">
          {children}
        </div>
      )}
    </section>
  );
}

function FactStrip({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/40">
      {children}
    </div>
  );
}

function Fact({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <div className="text-center">
      <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </div>
    </div>
  );
}

// Unused but exported helpers (kept to mirror the data shape for future use).
export { fmtKb, formatDate, daysUntil };